import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Paper,
  Divider,
  Stack,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import API from "../utils/api";
import { toast } from "react-toastify";
import { Autocomplete } from "@mui/material";
import { alpha } from "@mui/material/styles";


export default function AddFollowUpPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [formData, setFormData] = useState({
    lead: "",
    due_date: "",
    notes: "",
    created_by: "",
  });

  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    fetchLeads();
    fetchUsers();
  }, []);

  const fetchLeads = async () => {
    try {
      const res = await API.get("/leads/");
      setLeads(res.data);
    } catch (err) {
      console.error("Error fetching leads:", err);
      toast.error("Failed to load leads");
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await API.get("/users/");
      setUsers(res.data);
    } catch (err) {
      console.error("Error fetching users:", err);
      toast.error("Failed to load users");
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSave = async () => {
    if (!formData.lead || !formData.due_date || !formData.created_by) {
      toast.error("⚠️ Please fill all required fields");
      return;
    }

    try {
      await API.post("/followups/", formData);
      toast.success("Follow-Up added successfully!");
      navigate("/followups");
    } catch (err) {
      console.error("Error saving follow-up:", err.response?.data || err.message);
      toast.error("Failed to save follow-up");
    }
  };

  const handleCancel = () => navigate("/followups");

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        minHeight: "100vh",
        p: { xs: 0, md: 3 },
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, md: 4 },
          width: { xs: "calc(100vw - 5px)", md: "600px" },
          maxWidth: "100%",
          borderRadius: "16px", // Keep rounded corners on mobile
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
          mt: { xs: 0, md: 4 },
          mx: { xs: "2.5px", md: 0 },
        }}
      >
        {/* Unified Header */}
<Box
  sx={{
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    borderBottom: `2px solid ${theme.palette.primary.main}`,
    borderRadius: "12px 12px 0 0",
    px: 3,
    py: 2.5,
    mb: 3,
  }}
>
  <Typography
    variant="h6"
    sx={{
      color: "primary.main",
      fontWeight: 700,
      letterSpacing: 0.3,
    }}
  >
    Add Follow-Up
  </Typography>
  <Typography
    variant="body2"
    color="text.secondary"
    sx={{ mt: 0.5 }}
  >
    Create a new follow-up task for a lead and track its progress.
  </Typography>
</Box>

        <Divider sx={{ mb: 3, borderColor: theme.palette.divider }} />

        <Stack spacing={3}>
          {/* Lead dropdown */}
          <Box display="flex" gap={2}>
            <Autocomplete
              fullWidth
              options={leads}
              getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
              value={leads.find((l) => l.id === formData.lead) || null}
              onChange={(e, value) => handleChange("lead", value ? value.id : "")}
              renderInput={(params) => (
                <TextField {...params} label="Select Lead" variant="outlined" />
              )}
              sx={{ flex: 1 }}
            />
            <Button
              variant="outlined"
              color="primary"
              onClick={() => navigate("/add-lead")}
              sx={{ minWidth: 160, whiteSpace: "nowrap" }}
            >
              Add New Lead
            </Button>
          </Box>

          {/* Due Date */}
          <TextField
            type="date"
            fullWidth
            label="Due Date"
            InputLabelProps={{ shrink: true }}
            value={formData.due_date}
            onChange={(e) => handleChange("due_date", e.target.value)}
            variant="outlined"
          />

          {/* Notes */}
          <TextField
            fullWidth
            multiline
            minRows={5}
            label="Notes"
            value={formData.notes}
            onChange={(e) => handleChange("notes", e.target.value)}
            variant="outlined"
          />

          {/* Created By */}
          <TextField
            select
            fullWidth
            label="Created By"
            value={formData.created_by}
            onChange={(e) => handleChange("created_by", e.target.value)}
            variant="outlined"
          >
            {users.map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {u.username}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        {/* Footer Buttons */}
        <Box
          display="flex"
          justifyContent="space-between"
          gap={2}
          mt={4}
         
          pt={0}
          sx={{ backgroundColor: "inherit" }}
        >
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleCancel}
            sx={{ width: "48%" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            sx={{ width: "48%" }}
          >
            Save Follow-Up
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
